/** 旧系统合同占位符目录；模板编辑与发布校验共用，字段由系统维护。 */
export const legacyContractVariables = [
  { key: 'order_id', label: '订单编号' },
  { key: 'setting.company', label: '甲方公司' },
  { key: 'setting.legal_person', label: '甲方法人' },
  { key: 'setting.id_card', label: '甲方证件' },
  { key: 'setting.phone', label: '甲方电话' },
  { key: 'company_name', label: '乙方公司' },
  { key: 'legal_person', label: '乙方法人' },
  { key: 'id_card', label: '乙方证件' },
  { key: 'phone_number', label: '乙方电话' },
  { key: 'address', label: '租赁地址' },
  { key: 'area', label: '租赁面积' },
  { key: 'range_date', label: '租期' },
  { key: 'start_date', label: '开始租期' },
  { key: 'rent', label: '租赁金额（元）' }
] as const

export function isLegacyContractVariableKey(key: string) {
  return legacyContractVariables.some((variable) => variable.key === key)
}

type ContractVariableDefinition = {
  key: string
  label: string
  description: string
  aliases: readonly string[]
  supportedTypes: readonly string[]
}

const allTypes = ['lease', 'service', 'housing_proof', 'supplement', 'other'] as const
const rentalTypes = ['lease', 'housing_proof', 'supplement', 'other'] as const
const serviceTypes = ['service', 'supplement', 'other'] as const

/** 本系统字段是插入入口；别名仅用于识别、校验和填充历史模板。 */
export const systemContractVariables: readonly ContractVariableDefinition[] = [
  {
    key: 'orderNo',
    label: '订单编号',
    description: '来源订单的业务编号',
    aliases: ['order.no', 'order.orderNo', 'order_no'],
    supportedTypes: allTypes
  },
  {
    key: 'order.id',
    label: '订单 ID',
    description: '来源订单的数字主键；旧 order_id 保持原有 ID 语义',
    aliases: ['order_id'],
    supportedTypes: allTypes
  },
  {
    key: 'contract.partyName',
    label: '乙方签约名称',
    description: '本次合同选定的签约名称，优先于企业、个人及客户名称',
    aliases: ['company_name', 'party_name'],
    supportedTypes: allTypes
  },
  { key: 'customer.name', label: '客户名称', description: '订单所属客户名称', aliases: [], supportedTypes: allTypes },
  {
    key: 'customer.phone',
    label: '乙方联系电话',
    description: '订单所属客户的联系电话',
    aliases: ['phone_number'],
    supportedTypes: allTypes
  },
  {
    key: 'organization.name',
    label: '甲方公司',
    description: '订单保存的经营主体名称',
    aliases: ['setting.company'],
    supportedTypes: allTypes
  },
  {
    key: 'organization.fullAddress',
    label: '甲方注册地址',
    description: '经营主体档案的注册地址',
    aliases: [],
    supportedTypes: allTypes
  },
  {
    key: 'organization.legalRepresentative',
    label: '甲方法人',
    description: '经营主体法定代表人姓名',
    aliases: ['setting.legal_person'],
    supportedTypes: allTypes
  },
  {
    key: 'organization.legalRepresentativeIdNumber',
    label: '甲方法人证件',
    description: '经营主体法定代表人证件号码',
    aliases: ['setting.id_card'],
    supportedTypes: allTypes
  },
  {
    key: 'organization.unifiedSocialCreditCode',
    label: '甲方统一社会信用代码',
    description: '经营主体工商登记代码',
    aliases: [],
    supportedTypes: allTypes
  },
  {
    key: 'legalRepresentative.name',
    label: '乙方法人姓名',
    description: '企业档案中的法人姓名；未填写时使用关联身份姓名',
    aliases: ['legal_person'],
    supportedTypes: allTypes
  },
  {
    key: 'identity.name',
    label: '乙方身份姓名',
    description: '订单关联的个人身份姓名',
    aliases: [],
    supportedTypes: allTypes
  },
  {
    key: 'identity.idNumber',
    label: '乙方证件号码',
    description: '订单关联的个人身份证件号码',
    aliases: ['id_card'],
    supportedTypes: allTypes
  },
  {
    key: 'company.name',
    label: '乙方企业名称',
    description: '订单关联的企业工商名称',
    aliases: [],
    supportedTypes: allTypes
  },
  {
    key: 'company.unifiedSocialCreditCode',
    label: '乙方统一社会信用代码',
    description: '订单关联企业的工商登记代码',
    aliases: [],
    supportedTypes: allTypes
  },
  {
    key: 'company.registeredCapital',
    label: '乙方注册资本',
    description: '订单关联企业的注册资本',
    aliases: [],
    supportedTypes: allTypes
  },
  {
    key: 'order.amount.numeric',
    label: '订单金额（元）',
    description: '租赁订单约定租金；服务订单约定金额或按收费规则计算的服务费用',
    aliases: ['order.agreedPrice', 'rent'],
    supportedTypes: allTypes
  },
  {
    key: 'lease.address',
    label: '租赁地址',
    description: '订单地址快照，缺失时使用场地和房间地址',
    aliases: ['address'],
    supportedTypes: rentalTypes
  },
  {
    key: 'lease.area',
    label: '租赁面积',
    description: '订单关联房间的面积',
    aliases: ['area'],
    supportedTypes: rentalTypes
  },
  {
    key: 'lease.roomNo',
    label: '房间号',
    description: '订单关联房间的编号',
    aliases: ['room_no'],
    supportedTypes: rentalTypes
  },
  {
    key: 'lease.dateRange',
    label: '租期',
    description: '租赁开始日期至结束日期',
    aliases: ['range_date'],
    supportedTypes: rentalTypes
  },
  {
    key: 'lease.startDate',
    label: '租赁开始日期',
    description: '租赁订单开始日期',
    aliases: ['start_date'],
    supportedTypes: rentalTypes
  },
  {
    key: 'lease.endDate',
    label: '租赁结束日期',
    description: '租赁订单结束日期',
    aliases: ['end_date'],
    supportedTypes: rentalTypes
  },
  {
    key: 'lease.priceText',
    label: '租金说明',
    description: '约定租金与日、月或年计价周期',
    aliases: [],
    supportedTypes: rentalTypes
  },
  {
    key: 'lease.deposit',
    label: '租赁押金（元）',
    description: '租赁收费规则中的押金金额',
    aliases: ['deposit'],
    supportedTypes: rentalTypes
  },
  {
    key: 'service.startDate',
    label: '服务开始日期',
    description: '期限服务的首个服务日',
    aliases: [],
    supportedTypes: serviceTypes
  },
  {
    key: 'service.endDate',
    label: '服务结束日期',
    description: '期限服务约定的最后服务日（含当天）',
    aliases: [],
    supportedTypes: serviceTypes
  },
  {
    key: 'service.priceText',
    label: '服务费用说明',
    description: '收费项目、单价、计价单位、周期及费用适用期间',
    aliases: [],
    supportedTypes: serviceTypes
  }
]

export function getContractVariable(key: string) {
  const variable = systemContractVariables.find((item) => item.key === key || item.aliases.includes(key))
  if (variable) return { ...variable, key }
  return legacyContractVariables.find((item) => item.key === key)
}

/** 从同一组业务值生成兼容别名，避免新旧字段各自取值而产生差异。 */
export function withContractVariableAliases(values: Partial<Record<string, string>>) {
  const result = { ...values }
  for (const variable of systemContractVariables) {
    const value = values[variable.key]
    if (value === undefined) continue
    for (const alias of variable.aliases) {
      result[alias] = value
    }
  }
  return result
}

/** 历史字段保留原发布规则；本系统字段按适用合同类型校验。 */
export function validateContractVariableKeys(keys: readonly string[], templateType: string) {
  const missingKeys: string[] = []
  const unsupportedKeys: string[] = []
  for (const key of keys) {
    if (isLegacyContractVariableKey(key)) continue
    const variable = systemContractVariables.find((item) => item.key === key || item.aliases.includes(key))
    if (!variable) {
      missingKeys.push(key)
    } else if (!variable.supportedTypes.includes(templateType)) {
      unsupportedKeys.push(key)
    }
  }
  return { missingKeys, unsupportedKeys }
}
